/**
 * On Body — where a ring (or a necklace) sits in a photograph.
 *
 * The ONE copy of the anchor math (placements v2). Four callers share it:
 *
 *   - `scripts/onbody/detect.html` runs MediaPipe over the library and calls
 *     `measureHand` here for every hand it finds — the page imports this file
 *     bundled by esbuild (`anchorMath.mjs`, see build-placements.mjs), so the
 *     edge walk exists in exactly one place;
 *   - `scripts/onbody/build-placements.mjs` turns the detector's raw output
 *     (`detections.raw.json`) into `public/onbody/placements.json`;
 *   - the studio's On Body panel measures a photo the user brings
 *     (`detectHands.ts` → `measureHand` → `placeHand`) and a hand-clicked finger
 *     (`measureFingerBetween`);
 *   - `scripts/check-onbody.ts` proves this module reproduces every entry of
 *     placements.json from the raw detections.
 *
 * Pure: no DOM, no React, no three.js. Every coordinate is in photo PIXELS
 * unless the name says otherwise; what placements.json stores and what the
 * viewer receives is NORMALISED (x / width, y / height, y down) and a width is a
 * fraction of the photo WIDTH — `OnBodyAnchor` in the contract.
 *
 * Why v2. The v1 file kept one hand's ring finger, measured once at 0.5 of
 * MCP → PIP. MediaPipe's MCP landmark sits INSIDE the hand (the knuckle joint,
 * not the web of the fingers), so 0.5 landed on the finger's base, where it
 * merges into the hand: the ring sat on the knuckle and read wider than the
 * finger. iJewel3D seats it at 0.65 (their "Along Finger" default) and so do
 * we; the width is the median of five walks across the finger around that
 * seat, never at the base.
 */
import type { OnBodyAnchor, OnBodyKind } from "../../../shared/api-contract";

export type Vec2 = [number, number];
export type Vec3t = [number, number, number];
export type RGB = [number, number, number];

// ─── The hand ───────────────────────────────────────────────────────────────

export type FingerName = "thumb" | "index" | "middle" | "ring" | "pinky";
export const FINGER_NAMES: readonly FingerName[] = ["thumb", "index", "middle", "ring", "pinky"];

/**
 * The bone a ring sits on, as MediaPipe hand landmark indices: base joint →
 * next joint (MCP → PIP; on the thumb MCP 2 → IP 3). iJewel's
 * `getRingAttachPositionForFinger` lerps exactly these pairs.
 */
export const FINGER_JOINTS: Record<FingerName, readonly [number, number]> = {
  thumb: [2, 3],
  index: [5, 6],
  middle: [9, 10],
  ring: [13, 14],
  pinky: [17, 18],
};

/**
 * The knuckle pairs whose spacing scales a finger's fallback width and its
 * plausibility window. The thumb has no neighbouring knuckle of its own; the
 * index–middle spacing stands in for it.
 */
const SPACING_PAIRS: Record<FingerName, readonly (readonly [number, number])[]> = {
  thumb: [[5, 9]],
  index: [[5, 9]],
  middle: [[9, 5], [9, 13]],
  ring: [[13, 9], [13, 17]],
  pinky: [[17, 13]],
};

/** MediaPipe's hand landmarks: wrist, index MCP, little-finger MCP. */
const WRIST = 0;
const INDEX_MCP = 5;
const PINKY_MCP = 17;

// ─── The constants, with where each came from ───────────────────────────────

/** Where a ring sits along MCP → PIP: iJewel's "Along Finger" default. */
export const ALONG_DEFAULT = 0.65;
/** iJewel's slider range: 0 (the knuckle) … 2 (a full phalanx past the joint). */
export const ALONG_RANGE = { min: 0, max: 2 } as const;
/** The five walks across the finger, around the seat. */
export const SEAT_TS: readonly number[] = [0.55, 0.6, 0.65, 0.7, 0.75];
/** The fallback: three walks across the PIP joint itself. */
export const JOINT_TS: readonly number[] = [0.95, 1, 1.05];
/** How many clean walks a width needs (of five at the seat, of three at the joint). */
const MIN_SEAT_WALKS = 2;
const MIN_JOINT_WALKS = 2;
/**
 * Finger width ≈ this × the knuckle spacing when no walk can be trusted: the
 * median width / spacing of the library's fingers whose seat walks WERE clean
 * (2026-10-04, 109 hands; ring 0.916 over 81 fingers). iJewel3D sizes every
 * ring at 0.875 × the 3-D middle–ring knuckle distance, which lands at
 * 0.87–1.02 × this spacing on their three free photos — the same answer.
 */
export const KNUCKLE_FACTOR: Record<FingerName, number> = {
  thumb: 0.88,
  index: 0.98,
  middle: 0.95,
  ring: 0.92,
  pinky: 0.85,
};
/** A walked width is believed only inside this window of the knuckle spacing. */
export const WIDTH_WINDOW = { min: 0.55, max: 1.35 } as const;
/** A walk may run this far (× knuckle spacing) before giving up, per side. */
const WALK_REACH = 0.85;
/**
 * An EDGE is an abrupt step: this much colour change (`cdist`) across
 * `STEP_SPAN` × the knuckle spacing (at least 2 px). A finger is a cylinder,
 * so its colour falls off gradually toward the sides — measured on the library,
 * ~0.05 per 3 px of shading against 0.3–0.6 at the silhouette — and a walk that
 * stops when the colour drifts from the centre's (v1) stops in the shading: on
 * `AI_hand_back_view_nude_nails_cream_bg` it read 57 px of a 95 px finger.
 */
const STEP = 0.2;
const STEP_SPAN = 0.03;
/** Past an edge, the colour from just inside it coming back within this much
 *  means the step was a line ON a surface, not the end of one. */
const SKIN_MATCH = 0.1;
/** How far past an edge (× knuckle spacing) to look for that. */
const LINE_SPAN = 0.08;

/** placements.json stores four decimals. */
export const round4 = (v: number) => Math.round(v * 1e4) / 1e4;
const r4v = (v: readonly number[]) => v.map(round4);

// ─── Pixels ─────────────────────────────────────────────────────────────────

/** Reads a decoded photo. `avg` is the mean of a (2r+1)² block, clipped to the
 *  image; null when the whole block is outside it. */
export interface PixelSampler {
  w: number;
  h: number;
  at(x: number, y: number): RGB | null;
  avg(x: number, y: number, r: number): RGB | null;
}

/** A sampler over RGBA bytes (`ImageData.data`). */
export function samplerFromRgba(d: ArrayLike<number>, w: number, h: number): PixelSampler {
  const at = (x: number, y: number): RGB | null => {
    x = Math.round(x);
    y = Math.round(y);
    if (x < 0 || y < 0 || x >= w || y >= h) return null;
    const i = (y * w + x) * 4;
    return [d[i], d[i + 1], d[i + 2]];
  };
  const avg = (x: number, y: number, r: number): RGB | null => {
    const s: RGB = [0, 0, 0];
    let n = 0;
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const p = at(x + dx, y + dy);
        if (!p) continue;
        s[0] += p[0];
        s[1] += p[1];
        s[2] += p[2];
        n++;
      }
    }
    return n ? [s[0] / n, s[1] / n, s[2] / n] : null;
  };
  return { w, h, at, avg };
}

/** Luma + chroma distance between two RGB triples, 0..~1.7. */
export const cdist = (a: RGB, b: RGB) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) / 255;
export const luma = (c: RGB) => 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2];

/**
 * Where a walk across the finger stopped:
 *  - `colour` — a real edge: an abrupt step to something else;
 *  - `crease` — a thin DARK line with the same skin on both sides: two
 *    touching fingers, or a fold;
 *  - `neighbour` — a step whose far side turns back into the skin the walk was
 *    on within `LINE_SPAN`: a neighbouring finger (or the palm) past a gap;
 *  - `border` — the photo ended first;
 *  - `max` — no step within reach (skin, or a blur, behind the finger).
 * Only `colour` is a width anyone should believe.
 */
export type EdgeKind = "colour" | "crease" | "neighbour" | "border" | "max";

export interface EdgeHit {
  len: number;
  hit: EdgeKind;
}

/**
 * Half-width of the finger along direction `n` from `P`, photo pixels: walk out
 * to the first abrupt colour step (see `STEP`) — the silhouette — and say what
 * lies beyond it. `span` is the step's width in pixels, `lineSpan` how far past
 * it to look for the same surface again.
 */
export function edgeWalk(S: PixelSampler, P: Vec2, n: Vec2, maxLen: number, span: number, lineSpan: number): EdgeHit {
  const at = (t: number) => S.avg(P[0] + n[0] * t, P[1] + n[1] * t, 1);
  const first = at(0);
  if (!first) return { len: 0, hit: "border" };
  const seen: RGB[] = [first];
  const g = Math.max(2, Math.round(span));
  for (let t = 1; t <= maxLen; t++) {
    const c = at(t);
    if (!c) return { len: t, hit: "border" };
    seen.push(c);
    const before = seen[Math.max(0, t - g)];
    if (cdist(c, before) <= STEP) continue;
    // The edge is the sharpest single step inside the window.
    let k = t, best = -1;
    for (let j = Math.max(1, t - g + 1); j <= t; j++) {
      const d = cdist(seen[j], seen[j - 1]);
      if (d > best) { best = d; k = j; }
    }
    const len = k - 0.5;
    for (let j = 1; j <= Math.max(2, Math.round(lineSpan)); j++) {
      const f = at(t + j);
      if (f && cdist(f, before) < SKIN_MATCH) return { len, hit: luma(c) < luma(before) ? "crease" : "neighbour" };
    }
    return { len, hit: "colour" };
  }
  return { len: maxLen, hit: "max" };
}

/** Two walks across the finger at `t` along the bone, one per side. */
export interface WalkPair {
  t: number;
  a: EdgeHit;
  b: EdgeHit;
}

/** Every walk taken across one finger, in photo PIXELS. */
export interface FingerWalks {
  /** One pair per `SEAT_TS` entry. */
  seat: WalkPair[];
  /** One pair per `JOINT_TS` entry. */
  joint: WalkPair[];
}

/**
 * Walk across the bone `p0 → p1` (photo pixels) at each `t`: perpendicular to
 * it, both ways, from the bone line. `spacing` (the knuckle spacing, px) scales
 * the reach, the step width and the line test.
 */
export function walkBone(S: PixelSampler, p0: Vec2, p1: Vec2, spacing: number, ts: readonly number[]): WalkPair[] {
  const seg: Vec2 = [p1[0] - p0[0], p1[1] - p0[1]];
  const len = Math.hypot(seg[0], seg[1]) || 1;
  const n: Vec2 = [-seg[1] / len, seg[0] / len];
  const maxLen = Math.max(4, Math.round(spacing * WALK_REACH));
  const span = spacing * STEP_SPAN;
  const lineSpan = spacing * LINE_SPAN;
  return ts.map((t) => {
    const P: Vec2 = [p0[0] + seg[0] * t, p0[1] + seg[1] * t];
    return {
      t,
      a: edgeWalk(S, P, n, maxLen, span, lineSpan),
      b: edgeWalk(S, P, [-n[0], -n[1]], maxLen, span, lineSpan),
    };
  });
}

// ─── One finger's width ─────────────────────────────────────────────────────

/** How a finger's width was arrived at. */
export type FitMethod =
  | "edge" //    the median of the clean walks around the seat
  | "joint" //   …none: the clean walks across the PIP joint
  | "knuckle" // …none either: the knuckle spacing × KNUCKLE_FACTOR
  | "manual" //  a person placed it (placements.overrides.json)
  | "clicks"; // the user's two clicks, measured from the photo

export interface FingerFit {
  /** Width, photo pixels (raw) or a fraction of the photo WIDTH (stored). */
  width: number;
  /** The clean walk widths the width is the median of (empty for a fallback). */
  widthSamples: number[];
  method: FitMethod;
  /** Sideways offset of the finger's centre from the bone line, same units as
   *  `width`, + along the bone's left-hand normal (−dy, dx). */
  shift: number;
}

const median = (v: readonly number[]) => {
  const s = [...v].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/** A pair is a width only when both sides stopped on a real edge and the sum
 *  is a plausible finger for this hand. */
const cleanPair = (w: WalkPair, spacing: number) => {
  const width = w.a.len + w.b.len;
  return (
    w.a.hit === "colour" &&
    w.b.hit === "colour" &&
    width > spacing * WIDTH_WINDOW.min &&
    width < spacing * WIDTH_WINDOW.max
  );
};

/**
 * The finger's width from its walks: the median of the clean walks around the
 * seat; else of the clean walks across the PIP joint; else the knuckle spacing
 * × `KNUCKLE_FACTOR`. The centre shift is the median of the same walks'
 * (a − b) / 2, so the ring is centred between the edges it was sized by.
 */
export function fingerFitFromWalks(walks: FingerWalks, spacing: number, finger: FingerName): FingerFit {
  for (const [list, need, method] of [
    [walks.seat, MIN_SEAT_WALKS, "edge"],
    [walks.joint, MIN_JOINT_WALKS, "joint"],
  ] as const) {
    const clean = list.filter((w) => cleanPair(w, spacing));
    if (clean.length >= need) {
      const widths = clean.map((w) => w.a.len + w.b.len);
      return {
        width: median(widths),
        widthSamples: widths,
        method,
        shift: median(clean.map((w) => (w.a.len - w.b.len) / 2)),
      };
    }
  }
  return { width: spacing * KNUCKLE_FACTOR[finger], widthSamples: [], method: "knuckle", shift: 0 };
}

// ─── One detected hand ──────────────────────────────────────────────────────

/** A MediaPipe landmark: image landmarks are x, y normalised to the image and
 *  z a relative depth (same scale as x); world landmarks are metres. */
export interface Landmark {
  x: number;
  y: number;
  z: number;
}

/** One hand as `detect.html` (and the studio) records it, before rounding. */
export interface RawHand {
  /** MediaPipe's label, as it gives it ("Left" / "Right"). */
  handedness: string | null;
  score: number | null;
  /** 21 image landmarks, [x, y, z] normalised (MediaPipe's own numbers). */
  image: Vec3t[];
  /** 21 world landmarks, [x, y, z] metres, camera-aligned: x right, y down,
   *  z away from the camera, origin at the hand's centre. */
  world: Vec3t[];
  walks: Record<FingerName, FingerWalks>;
}

/** One photo as `detect.html` records it in detections.raw.json. */
export interface RawDetection {
  name: string;
  url: string;
  w: number;
  h: number;
  hands: RawHand[];
}

const toVec3 = (l: Landmark): Vec3t => [l.x, l.y, l.z];

/** Image landmark `j` in photo pixels. */
const px = (image: readonly Vec3t[], j: number, W: number, H: number): Vec2 => [image[j][0] * W, image[j][1] * H];

/** The knuckle spacing that scales finger `f`, photo pixels. */
export function knuckleSpacing(image: readonly Vec3t[], f: FingerName, W: number, H: number): number {
  const d = SPACING_PAIRS[f].map(([a, b]) => {
    const pa = px(image, a, W, H), pb = px(image, b, W, H);
    return Math.hypot(pa[0] - pb[0], pa[1] - pb[1]);
  });
  return d.reduce((s, v) => s + v, 0) / d.length;
}

/**
 * Measure every finger of one detected hand: the walks across each finger's
 * bone around the seat and across its PIP joint. Nothing is decided here — the
 * width is chosen from the walks by `fingerFitFromWalks`, so the decision can
 * be re-made (and checked) without the photo.
 */
export function measureHand(
  image: readonly Landmark[],
  world: readonly Landmark[],
  W: number,
  H: number,
  S: PixelSampler,
  handedness: string | null = null,
  score: number | null = null,
): RawHand {
  const img = image.map(toVec3);
  const walks = {} as Record<FingerName, FingerWalks>;
  for (const f of FINGER_NAMES) {
    const [j0, j1] = FINGER_JOINTS[f];
    const spacing = knuckleSpacing(img, f, W, H);
    const p0 = px(img, j0, W, H), p1 = px(img, j1, W, H);
    walks[f] = { seat: walkBone(S, p0, p1, spacing, SEAT_TS), joint: walkBone(S, p0, p1, spacing, JOINT_TS) };
  }
  return { handedness, score, image: img, world: world.map(toVec3), walks };
}

// ─── The placement file ─────────────────────────────────────────────────────

/** One hand as placements.json stores it (normalised, four decimals). */
export interface PlacedHand {
  handedness: string | null;
  score: number | null;
  /** 21 image landmarks [x, y, z], normalised. Absent on a hand-placed entry. */
  image?: Vec3t[];
  /** 21 world landmarks [x, y, z], metres. Absent on a hand-placed entry. */
  world?: Vec3t[];
  /** A hand-placed entry's bones, [MCP, PIP] normalised, per finger it has. */
  joints?: Partial<Record<FingerName, [Vec2, Vec2]>>;
  /** Width per finger, as a fraction of the photo WIDTH. */
  fingers: Partial<Record<FingerName, FingerFit>>;
}

export interface NeckPlacement {
  p0: Vec2;
  p1: Vec2;
  center: Vec2;
  width: number;
  axis3?: [number, number, number];
  method?: string;
}

export interface PhotoPlacement {
  /** The photo's iJewel CDN URL — what a mirrored preset records as
   *  `values.sourceUrl`. */
  source: string;
  w: number;
  h: number;
  /** Every hand found, the one to wear the ring on FIRST ("Switch Hand" walks
   *  the rest). Empty when there is no hand. */
  hands: PlacedHand[];
  neck: NeckPlacement | null;
}

export interface PlacementsFile {
  version: number;
  generatedBy?: string;
  units?: string;
  photos: Record<string, PhotoPlacement>;
}

/** A raw hand, measured and rounded the way placements.json stores it. */
export function placeHand(raw: RawHand, W: number, H: number): PlacedHand {
  const fingers: PlacedHand["fingers"] = {};
  for (const f of FINGER_NAMES) {
    const fit = fingerFitFromWalks(raw.walks[f], knuckleSpacing(raw.image, f, W, H), f);
    fingers[f] = {
      width: round4(fit.width / W),
      widthSamples: fit.widthSamples.map((v) => round4(v / W)),
      method: fit.method,
      shift: round4(fit.shift / W),
    };
  }
  return {
    handedness: raw.handedness,
    score: raw.score == null ? null : round4(raw.score),
    image: raw.image.map((v) => r4v(v) as Vec3t),
    // A detector that gave no world landmarks leaves the hand without them:
    // the viewer then lays the finger in the picture and turns the head to the
    // camera, rather than reading a direction out of nothing.
    ...(raw.world.length === 21 ? { world: raw.world.map((v) => r4v(v) as Vec3t) } : {}),
    fingers,
  };
}

/** The bone of finger `f`, normalised [MCP, PIP], or null when the hand has none. */
export function boneOf(hand: PlacedHand, f: FingerName): [Vec2, Vec2] | null {
  const j = hand.joints?.[f];
  if (j) return j;
  if (!hand.image) return null;
  const [a, b] = FINGER_JOINTS[f];
  return [
    [hand.image[a][0], hand.image[a][1]],
    [hand.image[b][0], hand.image[b][1]],
  ];
}

/** The fingers this hand can wear a ring on (a hand-placed entry: its own). */
export const fingersOf = (hand: PlacedHand): FingerName[] =>
  FINGER_NAMES.filter((f) => !!hand.fingers[f] && !!boneOf(hand, f));

/**
 * The hand to wear the ring on: the longest visible ring-finger bone (the most
 * frontal hand). The FIRST of equals wins. Hands without a ring finger last.
 */
export function ringHandIndex(hands: readonly PlacedHand[], W: number, H: number): number | null {
  let best: number | null = null;
  let bestLen = -1;
  hands.forEach((h, i) => {
    const bone = boneOf(h, "ring");
    if (!bone) return;
    const len = Math.hypot((bone[1][0] - bone[0][0]) * W, (bone[1][1] - bone[0][1]) * H);
    if (len > bestLen) {
      bestLen = len;
      best = i;
    }
  });
  return best ?? (hands.length ? 0 : null);
}

/** `hands` with the ring hand first, the rest in detection order. */
export function orderHands(hands: PlacedHand[], W: number, H: number): PlacedHand[] {
  const i = ringHandIndex(hands, W, H);
  return i == null || i === 0 ? hands : [hands[i], ...hands.filter((_, k) => k !== i)];
}

/** An overrides file entry. `hands` replaces the detected hands outright (a
 *  hand-placed finger); `hand` picks which detected hand comes first; `neck`
 *  replaces the neck (`null` removes it). */
export interface PlacementOverride {
  hands?: PlacedHand[];
  hand?: number;
  neck?: NeckPlacement | null;
}

/**
 * Every photo's placement from the raw detections plus the hand-placed
 * overrides — exactly what build-placements.mjs writes under `photos`.
 *
 * Necks come from overrides only: on these close crops the pose model put
 * "necks" on tulle and on hands.
 */
export function buildPlacements(
  raw: readonly RawDetection[],
  overrides: Record<string, PlacementOverride> = {},
): Record<string, PhotoPlacement> {
  const photos: Record<string, PhotoPlacement> = {};
  for (const r of raw) {
    const o = overrides[r.name] ?? {};
    let hands = r.hands.map((h) => placeHand(h, r.w, r.h));
    if (o.hands) hands = o.hands;
    else if (o.hand != null && hands[o.hand]) hands = [hands[o.hand], ...hands.filter((_, k) => k !== o.hand)];
    else hands = orderHands(hands, r.w, r.h);
    photos[r.name] = { source: r.url, w: r.w, h: r.h, hands, neck: o.neck ?? null };
  }
  return photos;
}

// ─── Orientation ────────────────────────────────────────────────────────────

const sub3 = (a: Vec3t, b: Vec3t): Vec3t => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross3 = (a: Vec3t, b: Vec3t): Vec3t => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const unit3 = (a: Vec3t): Vec3t | null => {
  const l = Math.hypot(a[0], a[1], a[2]);
  return l > 1e-9 ? [a[0] / l, a[1] / l, a[2] / l] : null;
};

/** The finger's direction MCP → PIP from the WORLD landmarks (MediaPipe's
 *  camera-aligned frame: x right, y down, z away). */
export function fingerAxisWorld(hand: PlacedHand, f: FingerName): Vec3t | null {
  if (!hand.world) return null;
  const [a, b] = FINGER_JOINTS[f];
  return unit3(sub3(hand.world[b], hand.world[a]));
}

/**
 * The sign that turns `(index MCP − wrist) × (little MCP − wrist)` into the
 * BACK-of-hand normal, by MediaPipe's handedness label — the fallback for a
 * hand held flat (see `dorsalNormal`).
 *
 * In the photo's frame (x right, y down, z away), a LEFT hand with its back to
 * the camera and fingers up has its thumb on the image's right, the index
 * knuckle right of the little one, and that cross product pointing TOWARD the
 * camera: it is the back-of-hand normal as it stands. A right hand is the
 * mirror image. On these unmirrored photos MediaPipe 0.10.35's label is the
 * anatomical hand: it agrees with the skeleton's own palm side on 107 of the
 * library's 109 hands.
 */
export const DORSAL_SIGN: Record<"Left" | "Right", 1 | -1> = { Left: 1, Right: -1 };

/** Fingertips this far off the knuckle plane (× the index–little knuckle
 *  span) say which side is the palm. */
const CURL_MIN = 0.15;

/** Image landmarks in photo pixels, depth included (MediaPipe's image z is on
 *  x's scale, so it is multiplied by the photo WIDTH). */
const imagePx = (image: readonly Vec3t[], W: number, H: number): Vec3t[] => image.map((v) => [v[0] * W, v[1] * H, v[2] * W]);

/**
 * Which side of the knuckle plane (wrist, index MCP, little MCP) the palm is
 * on, read from the skeleton itself: fingers curl toward the palm, so the mean
 * fingertip offset along `(index MCP − wrist) × (little MCP − wrist)` is
 * positive when the palm is on that side. As a fraction of the index–little
 * knuckle span; ~0 for a hand held flat.
 */
export function palmSideCurl(points: readonly Vec3t[]): number {
  const n = unit3(cross3(sub3(points[INDEX_MCP], points[WRIST]), sub3(points[PINKY_MCP], points[WRIST])));
  const span = Math.hypot(...sub3(points[INDEX_MCP], points[PINKY_MCP]));
  if (!n || !(span > 0)) return 0;
  const tips = [8, 12, 16, 20];
  let s = 0;
  for (const t of tips) {
    const v = sub3(points[t], points[WRIST]);
    s += v[0] * n[0] + v[1] * n[1] + v[2] * n[2];
  }
  return s / tips.length / span;
}

/**
 * The back-of-hand normal in the photo's frame (x right, y down, z away from
 * the camera), or null for a hand without landmarks.
 *
 * Built from the IMAGE landmarks (with their relative depth), not the world
 * ones. The two are separate MediaPipe outputs and on this library they do not
 * agree: the world plane is more than 45° off the image plane on 8 of 99 ring
 * hands, and it is the world plane that is wrong where the photo can say —
 * `AI_hand_near_chin_curly_hair_earring`, `AI_hand_near_collarbone_white_top`
 * and `AI_hand_grasping_white_tulle` are plainly backs of hands, and the world
 * plane turns all three edge-on (the ring rendered as a hook). Through the
 * image landmarks 103 of 109 hands face the camera, which is what a library of
 * back-of-hand shots should give.
 *
 * The skeleton decides which side is the back — the side the fingers do NOT
 * curl toward; only a hand held flat (curl under `CURL_MIN`) falls back to the
 * label (`DORSAL_SIGN`).
 */
export function dorsalNormal(hand: PlacedHand, photo: { w: number; h: number }): Vec3t | null {
  if (!hand.image) return null;
  const pts = imagePx(hand.image, photo.w, photo.h);
  const n = unit3(cross3(sub3(pts[INDEX_MCP], pts[WRIST]), sub3(pts[PINKY_MCP], pts[WRIST])));
  if (!n) return null;
  const curl = palmSideCurl(pts);
  const label = hand.handedness === "Left" || hand.handedness === "Right" ? hand.handedness : null;
  const s = Math.abs(curl) >= CURL_MIN ? (curl > 0 ? -1 : 1) : label ? DORSAL_SIGN[label] : null;
  return s == null ? null : [n[0] * s, n[1] * s, n[2] * s];
}

// ─── The anchor handed to the viewer ────────────────────────────────────────

/**
 * The anchor for finger `f` of `hand`: the bone (p0 = MCP, p1 = PIP), the seat
 * `along` the bone, shifted sideways to the middle of the edges the width was
 * measured between, the width, the finger's direction with both of MediaPipe's
 * depth cues (`axis3` from the image landmarks, `axisWorld` from the world
 * ones — the viewer believes a pitch only where they agree) and the
 * back-of-hand normal (`dorsalNormal`). `center` already sits at `along`, so a
 * v63 viewer (which ignores the v64 fields) seats it at the same place.
 */
export function fingerAnchor(
  photo: { w: number; h: number },
  hand: PlacedHand,
  f: FingerName,
  along = ALONG_DEFAULT,
): OnBodyAnchor | null {
  const bone = boneOf(hand, f);
  const fit = hand.fingers[f];
  if (!bone || !fit || !(fit.width > 0)) return null;
  const W = photo.w, H = photo.h;
  const p0: Vec2 = [bone[0][0] * W, bone[0][1] * H];
  const p1: Vec2 = [bone[1][0] * W, bone[1][1] * H];
  const seg: Vec2 = [p1[0] - p0[0], p1[1] - p0[1]];
  const len = Math.hypot(seg[0], seg[1]);
  if (!(len > 0)) return null;
  const n: Vec2 = [-seg[1] / len, seg[0] / len];
  const shift = fit.shift * W;
  const c: Vec2 = [p0[0] + seg[0] * along + n[0] * shift, p0[1] + seg[1] * along + n[1] * shift];
  const anchor: OnBodyAnchor = {
    p0: [bone[0][0], bone[0][1]],
    p1: [bone[1][0], bone[1][1]],
    center: [round4(c[0] / W), round4(c[1] / H)],
    width: fit.width,
    along,
  };
  if (hand.image) {
    const [a, b] = FINGER_JOINTS[f];
    // Image-landmark depth: the v63 out-of-plane cue, in photo pixels.
    const d = unit3([seg[0], seg[1], (hand.image[b][2] - hand.image[a][2]) * W]);
    if (d) anchor.axis3 = r4v(d) as Vec3t;
  }
  const axisWorld = fingerAxisWorld(hand, f);
  if (axisWorld) anchor.axisWorld = r4v(axisWorld) as Vec3t;
  const dorsal = dorsalNormal(hand, photo);
  if (dorsal) anchor.dorsal = r4v(dorsal) as Vec3t;
  return anchor;
}

/** The contract's anchor out of a stored neck placement (drops the bookkeeping). */
export function toAnchor(p: NeckPlacement): OnBodyAnchor {
  return {
    p0: [p.p0[0], p.p0[1]],
    p1: [p.p1[0], p.p1[1]],
    center: [p.center[0], p.center[1]],
    width: p.width,
    ...(p.axis3 ? { axis3: [p.axis3[0], p.axis3[1], p.axis3[2]] as [number, number, number] } : {}),
  };
}

// ─── Manual placement (clicks) ──────────────────────────────────────────────

/**
 * A ring finger's width per pixel of its MCP → PIP landmark span, when the
 * photo's pixels cannot be read: the median of the library's clean ring fingers
 * (0.393 over 81) — 17 mm of finger on ~41 mm of proximal phalanx.
 */
export const WIDTH_PER_BONE = 0.39;

/**
 * Two clicks — the knuckle (`p0`) and the middle joint (`p1`), photo pixels —
 * measured against the photo the way the library was: the same walks, with the
 * bone length standing in for the knuckle spacing (which needs a whole hand).
 * Null when the clicks coincide. Without pixels (`S` null — a cross-origin
 * photo the browser will not let us read) the width is `WIDTH_PER_BONE` × the
 * bone, which the panel then offers as a slider.
 */
export function measureFingerBetween(S: PixelSampler | null, p0: Vec2, p1: Vec2): FingerFit | null {
  const len = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
  if (!(len > 2)) return null;
  // The walks are scaled by a knuckle spacing; two clicks have none, so take
  // the spacing a finger of the typical width for this bone would have.
  const spacing = (len * WIDTH_PER_BONE) / KNUCKLE_FACTOR.ring;
  if (!S) return { width: len * WIDTH_PER_BONE, widthSamples: [], method: "knuckle", shift: 0 };
  const fit = fingerFitFromWalks(
    { seat: walkBone(S, p0, p1, spacing, SEAT_TS), joint: walkBone(S, p0, p1, spacing, JOINT_TS) },
    spacing,
    "ring",
  );
  return fit.method === "knuckle" ? { ...fit, width: len * WIDTH_PER_BONE } : { ...fit, method: "clicks" };
}

/** A hand-placed (or clicked) finger as a one-finger hand, normalised. */
export function manualHand(p0: Vec2, p1: Vec2, fit: FingerFit, W: number, H: number, f: FingerName = "ring"): PlacedHand {
  return {
    handedness: null,
    score: null,
    joints: { [f]: [[round4(p0[0] / W), round4(p0[1] / H)], [round4(p1[0] / W), round4(p1[1] / H)]] },
    fingers: {
      [f]: {
        width: round4(fit.width / W),
        widthSamples: fit.widthSamples.map((v) => round4(v / W)),
        method: fit.method,
        shift: round4(fit.shift / W),
      },
    },
  };
}

/** How wide a neck is per pixel of its base → chin span, without an edge click. */
const NECK_WIDTH_PER_SPAN = 1.0;

/**
 * A necklace's anchor from three clicks, photo PIXELS: `p0` the base of the
 * neck, `p1` the chin, `edge` any point on one side. The width is twice the
 * edge's distance from the p0 → p1 line; the necklace sits at the base of the
 * neck (the contract's convention for `center`). Null when p0 and p1 coincide.
 */
export function manualNeckAnchor(p0: Vec2, p1: Vec2, edge: Vec2 | null, W: number, H: number): OnBodyAnchor | null {
  const seg: Vec2 = [p1[0] - p0[0], p1[1] - p0[1]];
  const len = Math.hypot(seg[0], seg[1]);
  if (!(len > 0) || !(W > 0) || !(H > 0)) return null;
  const n: Vec2 = [-seg[1] / len, seg[0] / len];
  let half = edge ? Math.abs((edge[0] - p0[0]) * n[0] + (edge[1] - p0[1]) * n[1]) : 0;
  if (!(half > len * 0.05)) half = (len * NECK_WIDTH_PER_SPAN) / 2;
  return {
    p0: [p0[0] / W, p0[1] / H],
    p1: [p1[0] / W, p1[1] / H],
    center: [p0[0] / W, p0[1] / H],
    width: (2 * half) / W,
  };
}

/** Which body part a click sequence is for, and how many clicks it takes. */
export const CLICKS: Record<OnBodyKind, number> = { finger: 2, neck: 3 };
